import { IconWrapper, resolveCommunityForSite } from '@flexo/general';
import { getOrgRole, ORG_ROLES } from '@flexo/hooks';
import {
  navigateToCommunity,
  navigateToMember,
  navigateToSite,
} from '@flexo/resolvers';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import * as luxon from 'luxon';

import styles from './end-user-nav.module.scss';

type NavSection = 'home' | 'community';

/**
 * FLEXO-6066: end-user primary navigation.
 *
 * Renders as a fixed left sidebar from the SM breakpoint up and collapses into a
 * bottom footer below SM (mirroring the mobile footer). End-user only — returns
 * null for staff so admin/supervisor layouts are untouched. Icon names reuse the
 * mobile convention (`household` / `community`, with the `_active` variants).
 */
export function EndUserNav() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const pathSegment = location.pathname.split('/')[1] || '';
  const entityId = location.pathname.split('/')[2] || '';

  const user = useSelector((state: any) => state.user);
  const metadata = useSelector((state: any) => state.metadata);
  const calendar = useSelector((state: any) => state.calendar);

  const isEndUser = getOrgRole(user) === ORG_ROLES.END_USER;

  // Offset the page content (via a body class consumed in the stylesheet) so it
  // is not overlapped by the fixed nav. Scoped to end users only.
  useEffect(() => {
    if (!isEndUser) return;
    document.body.classList.add('enduser-nav-active');
    return () => document.body.classList.remove('enduser-nav-active');
  }, [isEndUser]);

  if (!isEndUser) return null;

  const sites = (metadata?.entities as any)?.site || [];
  const communities = (metadata?.entities as any)?.community || [];
  const flexoUserId =
    user?.metadata?.flexo_user_id || user?.tokenContent?.flexo_user_id;
  const dateFrom = calendar?.hiveCalendar?.selectedDate?.[0]
    ? luxon.DateTime.fromJSDate(
        calendar.hiveCalendar.selectedDate[0]
      ).toFormat('yyyy-MM-dd')
    : luxon.DateTime.now().toFormat('yyyy-MM-dd');

  const isCommunityActive =
    pathSegment === 'community' ||
    pathSegment === 'communities' ||
    pathSegment.startsWith('community-');
  const activeSection: NavSection = isCommunityActive ? 'community' : 'home';
  const siteCommunityIds = new Set(
    sites
      .map(
        (site: any) =>
          resolveCommunityForSite(site, dateFrom) || site?.communityId
      )
      .filter(Boolean)
  );
  const hasSitesInMultipleCommunities = siteCommunityIds.size > 1;

  const goEndUserHomeAggregate = () => {
    if (sites.length === 1 && sites[0]?.siteId) {
      navigateToSite('site', sites[0].siteId);
    } else if (flexoUserId) {
      navigateToMember('member', flexoUserId);
    } else if (sites[0]?.siteId) {
      navigateToSite('site', sites[0].siteId);
    }
  };

  const goHome = () => {
    if (pathSegment === 'communities') {
      goEndUserHomeAggregate();
      return;
    }

    if (pathSegment === 'community' && entityId) {
      const firstCommunitySite = sites.find((site: any) => {
        const siteCommunityId =
          resolveCommunityForSite(site, dateFrom) || site?.communityId;
        return siteCommunityId === entityId;
      });

      if (firstCommunitySite?.siteId) {
        navigateToSite('site', firstCommunitySite.siteId);
        return;
      }
    }

    goEndUserHomeAggregate();
  };

  const goCommunity = () => {
    if (pathSegment === 'member') {
      if (hasSitesInMultipleCommunities) {
        navigate(`/communities${location.search || ''}`);
        return;
      }

      const communityId =
        Array.from(siteCommunityIds)[0] ||
        communities[0]?.communityId ||
        communities[0]?.flexoCommunityId;
      if (communityId) navigateToCommunity('community', communityId);
      return;
    }

    if (pathSegment === 'site' && entityId) {
      const currentSite = sites.find((site: any) => site?.siteId === entityId);
      const communityId =
        resolveCommunityForSite(currentSite, dateFrom) ||
        currentSite?.communityId;

      if (communityId) {
        navigateToCommunity('community', communityId);
        return;
      }
    }

    if (hasSitesInMultipleCommunities) {
      navigate(`/communities${location.search || ''}`);
      return;
    }

    const communityId =
      Array.from(siteCommunityIds)[0] ||
      communities[0]?.communityId ||
      communities[0]?.flexoCommunityId;
    if (communityId) navigateToCommunity('community', communityId);
  };

  const items: {
    section: NavSection;
    icon: string;
    label: string;
    onClick: () => void;
  }[] = [
    {
      section: 'home',
      icon: 'household',
      label: t('dropdowns.navHome'),
      onClick: goHome,
    },
    {
      section: 'community',
      icon: 'community',
      label: t('dropdowns.navCommunity'),
      onClick: goCommunity,
    },
  ];

  return (
    <nav className={styles.EndUserNav} aria-label="primary">
      {items.map((item) => {
        const active = activeSection === item.section;
        return (
          <button
            key={item.section}
            type="button"
            title={item.label}
            className={`${styles.EndUserNav__Item} ${
              active ? styles.EndUserNav__Item__Active : ''
            }`}
            onClick={item.onClick}
          >
            <IconWrapper iconName={`${item.icon}${active ? '_active' : ''}`} />
            <span className={styles.EndUserNav__Label}>{item.label}</span>
          </button>
        );
      })}
    </nav>
  );
}

export default EndUserNav;
